/**
 * Medicine alarm scheduling.
 *
 * Replaces `checkPatientAlarms` plus the `firedKeys` / `snoozedUntil` bookkeeping
 * that lived on the global `window.capsuleAlarm` object. Scanning is driven by
 * the shared ticker instead of yet another uncleared `setInterval`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { alarmToRing, doseAlarmKey } from '../domain/medication';
import { useInterval } from './usePolling';
import { alarmService } from '../services/alarmService';

/** How often to look for a due dose. 30s is well inside the 60s tolerance window. */
const SCAN_INTERVAL_MS = 30000;

/** Snooze length, matching the legacy modal's button. */
export const SNOOZE_MINUTES = 10;

/** Keys already fired today, so a reload does not re-announce a taken dose. */
const FIRED_KEYS_STORAGE = 'capsule_notified_alarms';

function loadFiredKeys() {
  try {
    const raw = localStorage.getItem(FIRED_KEYS_STORAGE);
    if (!raw) return { date: '', keys: [] };
    const parsed = JSON.parse(raw);
    return { date: parsed.date || '', keys: Array.isArray(parsed.keys) ? parsed.keys : [] };
  } catch {
    return { date: '', keys: [] };
  }
}

function saveFiredKeys(dateKey, keys) {
  try {
    localStorage.setItem(FIRED_KEYS_STORAGE, JSON.stringify({ date: dateKey, keys: [...keys] }));
  } catch {
    /* non-critical */
  }
}

const localDateKey = (now = new Date()) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

/**
 * @param {Array} medicines   Records carrying `id`, `firstDoseTime`, `frequency`, `alarmEnabled`.
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]
 * @param {string|null} [options.ringtone]  Data URI for a custom ringtone.
 * @param {object|null} [options.mealTimes]  The patient's meal times, which
 *   "with meals" medicines ring at.
 */
export function useMedicineAlarm(medicines = [], options = {}) {
  const { enabled = true, ringtone = null, mealTimes = null } = options;

  const [activeAlarm, setActiveAlarm] = useState(null);
  const firedKeys = useRef(new Set());
  /** Snoozed doses, by key: the alarm to ring again and when. */
  const snoozes = useRef(new Map());
  const currentDay = useRef(localDateKey());

  // Restore today's fired keys once, so a refresh mid-day is not noisy.
  useEffect(() => {
    const stored = loadFiredKeys();
    if (stored.date === localDateKey()) firedKeys.current = new Set(stored.keys);
  }, []);

  useEffect(() => {
    alarmService.setCustomRingtone(ringtone);
  }, [ringtone]);

  useEffect(() => () => alarmService.stop(), []);

  const dismiss = useCallback(() => {
    alarmService.stop();
    setActiveAlarm(null);
  }, []);

  /**
   * Silence now, ring again in `SNOOZE_MINUTES`. The dose itself is due only
   * for a minute around its time, so the snooze is kept until it rings.
   */
  const snooze = useCallback(() => {
    if (activeAlarm?.key) {
      const entry = { ...activeAlarm };
      delete entry.snoozed;
      snoozes.current.set(activeAlarm.key, { entry, until: Date.now() + SNOOZE_MINUTES * 60 * 1000 });
    }
    alarmService.stop();
    setActiveAlarm(null);
  }, [activeAlarm]);

  /** Mark the dose taken: silence it and keep it suppressed for the rest of the day. */
  const markTaken = useCallback(() => {
    if (activeAlarm?.key) {
      firedKeys.current.add(activeAlarm.key);
      snoozes.current.delete(activeAlarm.key);
      saveFiredKeys(currentDay.current, firedKeys.current);
    }
    alarmService.stop();
    setActiveAlarm(null);
  }, [activeAlarm]);

  const scan = useCallback(() => {
    if (!enabled || activeAlarm) return;

    const now = new Date();
    const today = localDateKey(now);

    // A new day clears yesterday's history.
    if (today !== currentDay.current) {
      currentDay.current = today;
      firedKeys.current = new Set();
      saveFiredKeys(today, firedKeys.current);
    }

    const next = alarmToRing({
      medicines,
      firedKeys: firedKeys.current,
      snoozes: snoozes.current,
      now,
      mealTimes,
    });

    if (!next) return;

    snoozes.current.delete(next.key);
    firedKeys.current.add(next.key);
    saveFiredKeys(today, firedKeys.current);
    setActiveAlarm(next);
    alarmService.start();
  }, [enabled, activeAlarm, medicines, mealTimes]);

  useInterval(scan, enabled ? SCAN_INTERVAL_MS : null, {
    immediate: true,
    pauseWhenHidden: false, // an alarm must fire even on a backgrounded tab
  });

  /** Fire an alarm on demand — used by the "test alarm" control in settings. */
  const trigger = useCallback((med) => {
    setActiveAlarm({ med, seconds: 0, key: doseAlarmKey(med?.id, 0) });
    alarmService.start();
  }, []);

  return { activeAlarm, dismiss, snooze, markTaken, trigger };
}
